import type { SymbolKey } from '@/entities/symbol';
import { http } from '@/services/http';
import { enqueueIndexCall } from '@/services/http/indexQueuer';
import buildUrl from '@/shared/lib/url/buildUrl';

function combineCsvData(responses: string[]): string {
  if (!responses.length) return '';
  const [firstResponse, ...otherResponses] = responses;
  const [header, ...rows] = firstResponse.split('\n').filter(Boolean);
  const combinedRows = [...rows];
  for (const response of otherResponses) {
    const [, ...dataRows] = response.split('\n');
    combinedRows.push(...dataRows.filter(Boolean));
  }
  return [header, ...combinedRows].join('\n');
}

export type ExportPreviewListParams = {
  from: string;
  to: string | null;
  includeMetadata: boolean;
  isCSV: boolean;
  userTimezone: string;
};

export const exportPreviewApi = {
  async fetchIndexData(url: string, headers?: Record<string, string>) {
    return enqueueIndexCall(() => http.data.get(url, { headers }).then(({ data }) => data));
  },

  async fetchPreviewList(
    params: ExportPreviewListParams,
    symbols: SymbolKey[],
    headers?: Record<string, string>
  ): Promise<string> {
    const { from, to, includeMetadata, isCSV, userTimezone } = params;
    const symbolRequests = symbols.filter(({ Code }) => !!Code);

    const results = await Promise.all(
      symbolRequests.map(({ Code, Period, PeriodType, TimeRef }) => {
        const indexURL = buildUrl('/index', {
          code: Code,
          period: Period,
          periodType: PeriodType,
          timeRef: TimeRef,
          from,
          timezone: userTimezone,
          to,
          metadata: includeMetadata
        });
        return exportPreviewApi.fetchIndexData(indexURL, headers) as Promise<string>;
      })
    );

    if (isCSV) {
      return combineCsvData(results);
    }
    const flattenedData = results.flat();
    return JSON.stringify(flattenedData, null, 2);
  }
};
